import {
  RequirementWorkflowType,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";

export interface GetCustomDocumentStatusCardContentParams {
  status: WorkerRequirementStatus;
  requirementWorkflowType?: RequirementWorkflowType;
  isRunBackgroundCheckOnBookingEnabled: boolean;
}

export interface CustomDocumentStatusCardContent {
  title?: string;
  statusMessage?: string;
  pillLabel?: string;
}

/**
 * Get custom document status card content including title, status message, and pill label
 * for worker requirement statuses, specifically for Checkr background check workflows.
 *
 * @param params - Parameters including status, workflow type, and feature flag
 * @returns Object containing optional title, statusMessage, and pillLabel
 */
export function getCustomDocumentStatusCardContent(
  params: GetCustomDocumentStatusCardContentParams
): CustomDocumentStatusCardContent {
  const { status, requirementWorkflowType, isRunBackgroundCheckOnBookingEnabled } = params;

  // Only apply custom content for Checkr background checks when the feature is enabled
  if (
    requirementWorkflowType === RequirementWorkflowType.CHECKR_BACKGROUND_CHECK &&
    isRunBackgroundCheckOnBookingEnabled
  ) {
    // FIXME: Automatically disabled during ESLint config update
    switch (status) {
      case WorkerRequirementStatus.IN_REVIEW: {
        return {
          title: "Your background check is processing",
          statusMessage: "Background checks are usually processed within a couple of hours.",
          pillLabel: "Submitted",
        };
      }

      case WorkerRequirementStatus.APPROVED: {
        return {
          title: "Your background check has been approved",
        };
      }

      case WorkerRequirementStatus.REJECTED: {
        return {
          title: "Your background check couldn't be approved",
          statusMessage: "Our Operations team will contact you by email soon with next steps.",
          pillLabel: "Needs attention",
        };
      }

      default: {
        return {};
      }
    }
  }

  // Legacy behavior: handle rejected status when feature flag is disabled
  if (
    requirementWorkflowType === RequirementWorkflowType.CHECKR_BACKGROUND_CHECK &&
    status === WorkerRequirementStatus.REJECTED
  ) {
    return {
      statusMessage:
        "We're sorry, but your background check was not approved. Tap the button below to learn more.",
    };
  }

  return {};
}
